import type {
  ModelTier,
  ProviderRecommendation,
} from '../domain/types'

export type ModelRecord = {
  provider: string
  name: string
  modelId: string
  tier: ModelTier
  availability: 'stable' | 'preview' | 'self-hosted'
  license?: string
  sourceUrl: string
  verifiedAt: string
  note: string
}

export const MODEL_REGISTRY: ModelRecord[] = [
  {
    provider: 'OpenAI',
    name: 'GPT-5.6 Luna',
    modelId: 'gpt-5.6-luna',
    tier: 'direct',
    availability: 'stable',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-luna',
    verifiedAt: '2026-07-25',
    note: 'Efficient, high-volume, well-scoped work.',
  },
  {
    provider: 'OpenAI',
    name: 'GPT-5.6 Terra',
    modelId: 'gpt-5.6-terra',
    tier: 'standard',
    availability: 'stable',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-terra',
    verifiedAt: '2026-07-25',
    note: 'Balanced intelligence, latency, and cost.',
  },
  {
    provider: 'OpenAI',
    name: 'GPT-5.6 Sol',
    modelId: 'gpt-5.6-sol',
    tier: 'deep',
    availability: 'stable',
    sourceUrl:
      'https://developers.openai.com/api/docs/models/gpt-5.6-sol',
    verifiedAt: '2026-07-25',
    note: 'Frontier capability for difficult professional work.',
  },
  {
    provider: 'Anthropic',
    name: 'Claude Haiku 4.5',
    modelId: 'claude-haiku-4-5-20251001',
    tier: 'direct',
    availability: 'stable',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/overview',
    verifiedAt: '2026-07-25',
    note: 'Fast, high-volume, straightforward work.',
  },
  {
    provider: 'Anthropic',
    name: 'Claude Sonnet 5',
    modelId: 'claude-sonnet-5',
    tier: 'standard',
    availability: 'stable',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/overview',
    verifiedAt: '2026-07-25',
    note: 'Speed and intelligence balance for coding and agents.',
  },
  {
    provider: 'Anthropic',
    name: 'Claude Opus 5',
    modelId: 'claude-opus-5',
    tier: 'deep',
    availability: 'stable',
    sourceUrl:
      'https://platform.claude.com/docs/en/about-claude/models/overview',
    verifiedAt: '2026-07-25',
    note: 'Accuracy-first complex reasoning and advanced coding.',
  },
  {
    provider: 'Google',
    name: 'Gemini 3.5 Flash-Lite',
    modelId: 'gemini-3.5-flash-lite',
    tier: 'direct',
    availability: 'stable',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/models/gemini-3.5-flash-lite',
    verifiedAt: '2026-07-25',
    note: 'Fast, low-cost extraction, parsing, and execution.',
  },
  {
    provider: 'Google',
    name: 'Gemini 3.6 Flash',
    modelId: 'gemini-3.6-flash',
    tier: 'standard',
    availability: 'stable',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/models/gemini-3.6-flash',
    verifiedAt: '2026-07-25',
    note: 'Complex agentic and multimodal work.',
  },
  {
    provider: 'Google',
    name: 'Gemini 3.1 Pro',
    modelId: 'gemini-3.1-pro-preview',
    tier: 'deep',
    availability: 'preview',
    sourceUrl:
      'https://ai.google.dev/gemini-api/docs/models/gemini-3.1-pro-preview',
    verifiedAt: '2026-07-25',
    note: 'Advanced problem solving and precise multi-step tool use.',
  },
  {
    provider: 'OpenAI',
    name: 'gpt-oss-20b',
    modelId: 'gpt-oss-20b',
    tier: 'standard',
    availability: 'self-hosted',
    license: 'Apache-2.0 plus gpt-oss usage policy',
    sourceUrl: 'https://openai.com/open-models/',
    verifiedAt: '2026-07-25',
    note: 'Local text reasoning when runtime and task evals fit.',
  },
  {
    provider: 'OpenAI',
    name: 'gpt-oss-120b',
    modelId: 'gpt-oss-120b',
    tier: 'deep',
    availability: 'self-hosted',
    license: 'Apache-2.0 plus gpt-oss usage policy',
    sourceUrl: 'https://openai.com/open-models/',
    verifiedAt: '2026-07-25',
    note: 'Larger local reasoning candidate; hardware fit is required.',
  },
  {
    provider: 'Google',
    name: 'Gemma 4 E4B',
    modelId: 'gemma-4-e4b',
    tier: 'direct',
    availability: 'self-hosted',
    license: 'Apache-2.0',
    sourceUrl: 'https://ai.google.dev/gemma/docs/core',
    verifiedAt: '2026-07-25',
    note: 'Small multimodal on-device model.',
  },
  {
    provider: 'Qwen',
    name: 'Qwen3.6 35B-A3B',
    modelId: 'Qwen3.6-35B-A3B',
    tier: 'standard',
    availability: 'self-hosted',
    license: 'Apache-2.0',
    sourceUrl:
      'https://huggingface.co/Qwen/Qwen3.6-35B-A3B',
    verifiedAt: '2026-07-25',
    note: 'Efficient multimodal, thinking, and tool-use candidate.',
  },
  {
    provider: 'Mistral',
    name: 'Mistral Small 4',
    modelId: 'Mistral-Small-4-119B-2603',
    tier: 'standard',
    availability: 'self-hosted',
    license: 'Apache-2.0',
    sourceUrl:
      'https://huggingface.co/mistralai/Mistral-Small-4-119B-2603',
    verifiedAt: '2026-07-25',
    note: 'Sparse multimodal reasoning and function-calling candidate.',
  },
  {
    provider: 'DeepSeek',
    name: 'DeepSeek V4 Flash',
    modelId: 'DeepSeek-V4-Flash',
    tier: 'standard',
    availability: 'self-hosted',
    license: 'MIT',
    sourceUrl:
      'https://huggingface.co/deepseek-ai/DeepSeek-V4-Flash',
    verifiedAt: '2026-07-25',
    note: 'Economical long-context reasoning and tools candidate.',
  },
  {
    provider: 'DeepSeek',
    name: 'DeepSeek V4 Pro',
    modelId: 'DeepSeek-V4-Pro',
    tier: 'deep',
    availability: 'self-hosted',
    license: 'MIT',
    sourceUrl:
      'https://huggingface.co/deepseek-ai/DeepSeek-V4-Pro',
    verifiedAt: '2026-07-25',
    note: 'Complex coding and agent workloads on substantial hardware.',
  },
  {
    provider: 'Meta',
    name: 'Llama 4 Scout',
    modelId: 'Llama-4-Scout-17B-16E-Instruct',
    tier: 'standard',
    availability: 'self-hosted',
    license: 'Llama 4 Community License',
    sourceUrl:
      'https://huggingface.co/meta-llama/Llama-4-Scout-17B-16E-Instruct',
    verifiedAt: '2026-07-25',
    note: 'Long-context multimodal work; runtime capabilities vary.',
  },
]

export function recommendationsForTier(
  tier: ModelTier,
): ProviderRecommendation[] {
  return MODEL_REGISTRY.filter(
    (model) =>
      model.tier === tier &&
      model.availability !== 'self-hosted' &&
      ['OpenAI', 'Anthropic', 'Google'].includes(model.provider),
  ).map((model) => ({
    provider: model.provider,
    model: model.name,
    modelId: model.modelId,
    tier: model.tier,
    note: model.note,
  }))
}
